import type { ReactNode } from "react"
import { cn } from "@/lib/cn"
import type { ChartTheme } from "@/lib/chart/theme"

interface Props {
  theme: ChartTheme
  /** Pointer x in CSS pixels; the tooltip sits beside it, flipping sides past the midpoint. */
  x: number | null
  width: number
  children: ReactNode
}

/** Below this chart width the tooltip spans the top of the plot instead of following the cursor. */
const NARROW_WIDTH = 520

/** Hover readout that follows the cursor instead of covering one corner of the plot. */
export function ChartTooltip({ theme, x, width, children }: Props) {
  const anchor = x ?? 0
  const onLeft = anchor > width / 2
  return (
    <div
      role="status"
      aria-live="off"
      className={cn(
        "pointer-events-none absolute top-3 z-10 flex max-w-[min(24rem,calc(100%-2rem))] flex-wrap gap-x-3 gap-y-0.5 rounded-lg [&>*]:whitespace-nowrap border px-3 py-2 font-mono text-[11px] shadow-xl backdrop-blur",
        theme.classes.tooltip,
      )}
      style={
        width < NARROW_WIDTH
          ? { left: 8, right: 8 }
          : onLeft
            ? { right: Math.max(8, width - anchor + 16) }
            : { left: Math.max(8, anchor + 16) }
      }
    >
      {children}
    </div>
  )
}

/** Small colour key used beside series and indicator names. */
export function Swatch({ color, dashed = false }: { color: string; dashed?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-0.5 w-3 shrink-0 self-center rounded-full"
      style={dashed ? { backgroundImage: `linear-gradient(90deg, ${color} 60%, transparent 0)`, backgroundSize: "4px 2px" } : { backgroundColor: color }}
    />
  )
}
